import { ref } from 'vue'
import { apiClient, ApiError } from './apiClient'
import { rolesService } from './rolesService'
import { generalSettingsService } from './generalSettingsService'

import { getSessionUserRaw, setSessionUserRaw } from './authStorage'

const profileRevision = ref(0)
let cachedUsers = []

const STATUT_LABELS = {
  actif: 'Actif',
  inactif: 'Inactif',
  suspendu: 'Suspendu'
}

const STATUT_BADGES = {
  actif: 'bg-success',
  inactif: 'bg-secondary',
  suspendu: 'bg-warning'
}

const MAX_PHOTO_SIZE = 2 * 1024 * 1024
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

const photoUrlCache = new Map()

const normalizeUser = (user) => ({
  ...user,
  id: user.id,
  roleId: user.roleId,
  roleCode: user.roleCode,
  roleLabel: user.roleLabel,
  nomComplet: user.nomComplet || `${user.prenom || ''} ${user.nom || ''}`.trim(),
  initiales: user.initiales || `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase(),
  companyId: user.companyId ?? null,
  companyLabel: user.companyLabel || '—',
  profilePhotoPresent: Boolean(user.profilePhotoPresent),
  doubleAuth: Boolean(user.doubleAuth),
  photoUrl: user.photoUrl || null
})

const readSessionUser = () => {
  try {
    const raw = getSessionUserRaw()
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const writeSessionUser = (user) => {
  if (user) {
    setSessionUserRaw(JSON.stringify(user))
  } else {
    setSessionUserRaw(null)
  }
}

export const usersService = {
  setSessionUser(userSummary) {
    if (!userSummary) {
      writeSessionUser(null)
      return
    }
    writeSessionUser({
      id: userSummary.id,
      nom: userSummary.nom,
      prenom: userSummary.prenom,
      nomComplet: `${userSummary.prenom || ''} ${userSummary.nom || ''}`.trim(),
      email: userSummary.email,
      roleCode: userSummary.role,
      roleLabel: userSummary.roleLabel,
      statut: 'actif',
      initiales: `${userSummary.prenom?.[0] || ''}${userSummary.nom?.[0] || ''}`.toUpperCase(),
      companyId: userSummary.companyId ?? null,
      companyLabel: userSummary.companyLabel || '—',
      profilePhotoPresent: Boolean(userSummary.profilePhotoPresent),
      doubleAuth: Boolean(userSummary.doubleAuth),
      photoUrl: null
    })
    profileRevision.value++
  },

  async getAll(params = {}) {
    const data = await apiClient.get('/users', params)
    if (!Array.isArray(data)) {
      throw new ApiError('Réponse invalide du serveur (liste utilisateurs attendue).', 500)
    }
    cachedUsers = data.map(normalizeUser)
    return cachedUsers
  },

  async getSummary() {
    return apiClient.get('/users/summary')
  },

  async getById(id) {
    const data = await apiClient.get(`/users/${id}`)
    return normalizeUser(data)
  },

  async getMyProfile() {
    const data = await apiClient.get('/users/me')
    return normalizeUser(data)
  },

  applyProfileToSession(profile) {
    const sessionUser = readSessionUser()
    if (!sessionUser || !profile) return null

    const merged = normalizeUser({
      ...sessionUser,
      ...profile,
      roleCode: profile.roleCode || sessionUser.roleCode,
      roleLabel: profile.roleLabel || sessionUser.roleLabel,
      photoUrl: sessionUser.photoUrl || null
    })

    writeSessionUser(merged)
    profileRevision.value++
    return merged
  },

  getCachedAll() {
    return [...cachedUsers]
  },

  getCurrentUser() {
    const sessionUser = readSessionUser()
    if (!sessionUser) return null
    return normalizeUser({
      ...sessionUser,
      roleLabel: sessionUser.roleLabel || rolesService.getLabel(sessionUser.roleId)
    })
  },

  getProfileRevision() {
    return profileRevision.value
  },

  getPasswordMinLength() {
    return generalSettingsService.getCached()?.passwordMinLength || 8
  },

  setDoubleAuth(enabled) {
    return this.updateDoubleAuth(enabled)
  },

  async changePassword({ currentPassword, newPassword, confirmPassword }) {
    const minLength = this.getPasswordMinLength()

    if (!currentPassword?.trim()) return { error: 'current_required' }
    if (!newPassword?.trim()) return { error: 'new_required' }
    if (newPassword.length < minLength) return { error: 'too_short', minLength }
    if (newPassword !== confirmPassword) return { error: 'mismatch' }
    if (newPassword === currentPassword) return { error: 'same_password' }

    try {
      await apiClient.put('/users/me/password', {
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim()
      })
      return { success: true }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return { error: 'wrong_password' }
      }
      throw error
    }
  },

  async updateDoubleAuth(enabled) {
    const updated = await apiClient.patch('/users/me/double-auth', { enabled: !!enabled })
    const normalized = normalizeUser(updated)
    this.applyProfileToSession(normalized)
    return normalized
  },

  updateProfilePhoto(photoUrl) {
    const user = readSessionUser()
    if (!user) return null
    user.photoUrl = photoUrl || null
    user.profilePhotoPresent = Boolean(photoUrl)
    writeSessionUser(user)
    profileRevision.value++
    return normalizeUser(user)
  },

  removeProfilePhoto() {
    return this.updateProfilePhoto(null)
  },

  clearPhotoCache(userId = null) {
    if (userId != null) {
      const key = String(userId)
      const cached = photoUrlCache.get(key)
      if (cached) {
        URL.revokeObjectURL(cached)
        photoUrlCache.delete(key)
      }
      return
    }
    photoUrlCache.forEach((url) => URL.revokeObjectURL(url))
    photoUrlCache.clear()
  },

  async getProfilePhotoUrl(userId, forceReload = false) {
    const key = String(userId)
    if (!forceReload && photoUrlCache.has(key)) {
      return photoUrlCache.get(key)
    }
    this.clearPhotoCache(userId)
    try {
      const url = await apiClient.fetchBlob(`/users/${userId}/photo`)
      photoUrlCache.set(key, url)
      return url
    } catch {
      return null
    }
  },

  async uploadProfilePhoto(userId, file) {
    const updated = await apiClient.upload(`/users/${userId}/photo`, file)
    this.clearPhotoCache(userId)
    await this.syncSessionFromUserResponse(updated)
    return normalizeUser(updated)
  },

  async deleteProfilePhoto(userId) {
    const updated = await apiClient.delete(`/users/${userId}/photo`)
    this.clearPhotoCache(userId)
    await this.syncSessionFromUserResponse(updated)
    return normalizeUser(updated)
  },

  async syncSessionFromUserResponse(updatedUser) {
    const sessionUser = readSessionUser()
    if (!sessionUser || String(sessionUser.id) !== String(updatedUser.id)) {
      return
    }
    sessionUser.profilePhotoPresent = Boolean(updatedUser.profilePhotoPresent)
    if (updatedUser.profilePhotoPresent) {
      sessionUser.photoUrl = await this.getProfilePhotoUrl(updatedUser.id, true)
    } else {
      sessionUser.photoUrl = null
    }
    writeSessionUser(sessionUser)
    profileRevision.value++
  },

  async refreshCurrentUserPhoto(forceReload = false) {
    const sessionUser = readSessionUser()
    if (!sessionUser?.id) return null

    if (!sessionUser.profilePhotoPresent) {
      sessionUser.photoUrl = null
      writeSessionUser(sessionUser)
      profileRevision.value++
      return null
    }

    sessionUser.photoUrl = await this.getProfilePhotoUrl(sessionUser.id, forceReload)
    writeSessionUser(sessionUser)
    profileRevision.value++
    return sessionUser.photoUrl
  },

  validateProfilePhoto(file) {
    if (!file) return { error: 'no_file' }
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) return { error: 'invalid_type' }
    if (file.size > MAX_PHOTO_SIZE) return { error: 'too_large', maxSize: MAX_PHOTO_SIZE }
    return { success: true }
  },

  getMaxPhotoSizeMb() {
    return MAX_PHOTO_SIZE / (1024 * 1024)
  },

  async updateMyProfile(data) {
    const updated = await apiClient.put('/users/me/profile', {
      nom: data.nom?.trim(),
      prenom: data.prenom?.trim(),
      telephone: data.telephone?.trim() || '',
      notes: data.notes?.trim() || ''
    })
    const normalized = normalizeUser(updated)
    this.applyProfileToSession(normalized)
    return normalized
  },

  countByRole(roleId) {
    return cachedUsers.filter((u) => String(u.roleId) === String(roleId)).length
  },

  countByCompany(companyId) {
    return cachedUsers.filter((u) => String(u.companyId) === String(companyId)).length
  },

  isLastAdmin(userId, users = cachedUsers) {
    const admins = users.filter((u) => u.roleCode === 'admin' && u.statut === 'actif')
    return admins.length === 1 && String(admins[0].id) === String(userId)
  },

  isAdminRole(roleId, roles = []) {
    const role = roles.find((r) => String(r.id) === String(roleId))
    return role?.code === 'admin'
  },

  async create(data) {
    const created = await apiClient.post('/users', {
      nom: data.nom?.trim(),
      prenom: data.prenom?.trim(),
      email: data.email?.trim(),
      telephone: data.telephone?.trim() || '',
      roleId: Number(data.roleId),
      companyId: Number(data.companyId),
      statut: data.statut || 'actif',
      notes: data.notes?.trim() || '',
      password: data.password
    })
    return normalizeUser(created)
  },

  async update(id, data) {
    const payload = {
      nom: data.nom?.trim(),
      prenom: data.prenom?.trim(),
      email: data.email?.trim(),
      telephone: data.telephone?.trim() || '',
      roleId: Number(data.roleId),
      companyId: Number(data.companyId),
      statut: data.statut || 'actif',
      notes: data.notes?.trim() || ''
    }
    if (data.password) payload.password = data.password

    const updated = await apiClient.put(`/users/${id}`, payload)
    return normalizeUser(updated)
  },

  async delete(id) {
    await apiClient.delete(`/users/${id}`)
    return true
  },

  async toggleStatut(id) {
    const updated = await apiClient.patch(`/users/${id}/toggle-statut`)
    return normalizeUser(updated)
  },

  getStatutLabel(statut) {
    return STATUT_LABELS[statut] || statut
  },

  getStatutBadge(statut) {
    return STATUT_BADGES[statut] || 'bg-secondary'
  }
}
